
type OrderDataLike = CashBoxOrderSummary | CashBoxOrderDetails | null | undefined;

type OrderItemLike = CashBoxOrderItem | CashBoxCustomOrderItem | CashBoxManualOrderItem | CashBoxOrderSummaryItemSource;

// const toNumber = (value) => Number(value || 0);
const toNumber = (value?: CashBoxNumeric | null): number => parseFloat(String(value || 0));

export const shouldShow = (condition?: unknown): boolean => Boolean(condition);
export const shouldHide = (condition?: unknown): boolean => !Boolean(condition);
export const shouldDisable = (condition?: unknown): boolean => Boolean(condition);
export const shouldEnable = (condition?: unknown): boolean => !Boolean(condition);

export const DELIVERY_CANCELLABLE_STATUSES: readonly string[] = Object.freeze([
    "pending",
    "created",
    "accepted",
    "arrival",
    "late_arrival",
    "reserved",
    "reserve_approved",
]);

// const NON_ACCEPTANCE_STATUSES = ["accepted", "processing", "delivered", "rejected"];


export const isDeliveryOrder = (orderDetails?: OrderDataLike): boolean =>
    orderDetails?.order_type === "delivery";

export const isPickupOrder = (orderDetails?: OrderDataLike): boolean =>
    orderDetails?.order_type === "pickup";

export const isDeliveryOrPickupOrder = (orderDetails?: OrderDataLike): boolean => (
    isDeliveryOrder(orderDetails) ||
    isPickupOrder(orderDetails)
);

export const isWaiterCreatedOrder = (orderDetails?: OrderDataLike): boolean =>
    Boolean(orderDetails?.waiter_id);


export const isOrderSuspended = (orderDetails?: OrderDataLike): boolean =>
    orderDetails?.order_status_key === "suspended";

export const isOrderDelivered = (orderDetails?: OrderDataLike): boolean =>
    orderDetails?.order_status_key === "delivered";

export const isOrderRejected = (orderDetails?: OrderDataLike): boolean =>
    orderDetails?.order_status_key === "rejected";

export const isOrderPaid = (orderDetails?: OrderDataLike): boolean =>
    orderDetails?.payment_status === "paid";

// export const orderHasRemainingPrice = (orderDetails?: OrderDataLike): boolean => (
//     isDetailedOrderData(orderDetails) &&
//     Number(orderDetails.cost) !== Number(orderDetails.remaining_cost)
// );


// export const needsOrderAcceptanceInDeliveryModal = (orderDetails) => {
//     // const statusKey = orderDetails?.order_status_key);
//     // const statusTitle = orderDetails?.order_status_title);

//     // if (!statusKey && !statusTitle) {
//     //     return false;
//     // }

//     // return (
//     //     !NON_ACCEPTANCE_STATUSES.includes(statusKey) &&
//     //     !NON_ACCEPTANCE_STATUSES.includes(statusTitle)
//     // );

//     return (
//         isDeliveryOrPickupOrder(orderDetails) &&
//         orderDetails?.order_status_key) === "suspended"
//     );
// };

// const isDetailedOrderData = (orderDetails: StepManagementState["currentOrderDetails"]) => (
export const isDetailedOrderData = (orderDetails: OrderDataLike): orderDetails is CashBoxOrderDetails => (
    !!orderDetails &&
    typeof orderDetails === "object" &&
    "waiter_id" in orderDetails &&
    "pickup_datetime" in orderDetails &&
    (
        "customItems" in orderDetails ||
        "freeItems" in orderDetails ||
        "checkouts" in orderDetails ||
        "delivery" in orderDetails
    )
);

export const needsOrderAcceptance = (orderDetails?: OrderDataLike): boolean => (
    isDetailedOrderData(orderDetails) &&
    isDeliveryOrPickupOrder(orderDetails) && (
        (!isWaiterCreatedOrder(orderDetails) && isOrderSuspended(orderDetails)) ||
        (isWaiterCreatedOrder(orderDetails) && !orderDetails?.pickup_datetime)
    )
);

export const shouldShowOrderPreProcessBox = (orderDetails?: OrderDataLike): boolean => (
    needsOrderAcceptance(orderDetails)
);

export const canWaiterEditUnconfirmedOrder = (orderDetails?: OrderDataLike): boolean => {
    return isWaiterCreatedOrder(orderDetails) && needsOrderAcceptance(orderDetails);
};

export const hasCourierRequestBeenSent = (orderDetails?: OrderDataLike): boolean => (
    isDetailedOrderData(orderDetails) &&
    Boolean(orderDetails.delivery) &&
    // Boolean(orderDetails?.delivery?.delivery_json_data) &&
    orderDetails.delivery?.delivery_status !== "pending"
);

export const hasPickupDateBeenSet = (orderDetails?: OrderDataLike): boolean => Boolean(
    // // isDetailedOrderData(orderDetails) &&
    // isPickupOrder(orderDetails) &&
    // // isWaiterCreatedOrder(orderDetails) &&
    isDetailedOrderData(orderDetails) &&
    orderDetails.pickup_datetime
);

export const hasOrderItems = (orderDetails?: OrderDataLike): boolean => Boolean(
    orderDetails?.items?.length ||
    (
        isDetailedOrderData(orderDetails) &&
        (
            orderDetails.customItems?.length ||
            orderDetails.freeItems?.length
        )
    )
);

export const canEditOrderOrAddItemsToIt = (orderDetails?: OrderDataLike): boolean => (
    !isOrderRejected(orderDetails) && (
        !isDeliveryOrPickupOrder(orderDetails) ||
        canWaiterEditUnconfirmedOrder(orderDetails)
    )
);

export const getOrderEditLockReasonKey = (orderDetails?: OrderDataLike): string => {
    return (
        !isDetailedOrderData(orderDetails)
            ? "" :
        canEditOrderOrAddItemsToIt(orderDetails)
            ? "" :
        isOrderRejected(orderDetails)
            ? "editingLockedForRejectedOrder" :
        !isWaiterCreatedOrder(orderDetails)
            ? "editingLockedForCustomerOrder"
            : "editingLockedAfterConfirmation"
    )
};

export const canEditOrderItemByPermission = (
    itemDetails?: OrderItemLike,
    waiterPermissions?: WaiterPermissions
): boolean => (
    !!itemDetails &&
    "is_paid" in itemDetails &&
    // !itemDetails?.is_done &&
    !itemDetails?.is_paid &&
    !itemDetails?.checkout_id &&
    (
        waiterPermissions?.edit_order_item !== false ||
        !itemDetails?.is_sent_to_kitchen
    )
);

export const getOrderItemEditLockReasonKey = (
    orderDetails?: OrderDataLike,
    itemDetails?: OrderItemLike,
    waiterPermissions?: WaiterPermissions
): string => (
    getOrderEditLockReasonKey(orderDetails) ||
    // (
    (
        (!itemDetails || !("is_paid" in itemDetails))
            ? "" :
        // itemDetails?.is_done
        //     ? "editingDoneOrderItemLocked" :
        itemDetails?.is_paid
            ? "editingPaidOrderItemLocked" :
        itemDetails?.checkout_id
            ? "editingPendingPaymentOrderItemLocked" :
        !canEditOrderItemByPermission(itemDetails, waiterPermissions)
            ? "editingSentToKitchenItemLockedByPermission"
            : ""
    )
);

export const canBePaidAgain = (
    item: {
        checkout_id?: CashBoxId | null;
        count: number;
        is_paid?: boolean;
        remaining_price: CashBoxNumeric;
        total_price: string;
    } | null,
    orderDetails: CashBoxOrderDetails,
    pendingPaymentNowMs: number,
    outOfPendingStatusCheckouts: Record<string, CashBoxPendingPaymentCheckoutInfo>,
    isExpiredPendingPaymentCheckout: (checkout?: Pick<CashBoxCheckout, "payment_status" | "payment_type" | "updated_at"> | null | undefined, nowMs?: number) => boolean,
    getOrderCheckout: (orderDetails?: {
    checkouts?: CashBoxCheckout[] | null | undefined;
} | null | undefined, checkoutId?: CashBoxId | null) => CashBoxCheckout | null,
) => {
    if (!item || item.is_paid || item.count < 1) {
        return false;
    }

    if (!item.checkout_id || Number(item.total_price).toFixed(2) !== Number(item.remaining_price).toFixed(2)) {
        return true;
    }

    if (outOfPendingStatusCheckouts?.[item.checkout_id]) {
        return false;
    }

    return isExpiredPendingPaymentCheckout(
        getOrderCheckout(orderDetails, item.checkout_id),
        pendingPaymentNowMs
    );
};

export const shouldHidePaymentButton = (orderDetails?: OrderDataLike): boolean =>
    isDeliveryOrder(orderDetails) || (
        isDetailedOrderData(orderDetails) &&
        isPickupOrder(orderDetails) &&
        !Number(orderDetails.remaining_cost)
    );

export const shouldDisablePaymentButton = (orderDetails?: OrderDataLike): boolean => {
    if (!isDetailedOrderData(orderDetails)) return true;

    const remainingCost = toNumber(orderDetails.remaining_cost);

    return (
        shouldHidePaymentButton(orderDetails) ||
        isOrderRejected(orderDetails) ||
        // (isDeliveryOrder(orderDetails) && !orderDetails.delivery) ||
        // (isPickupOrder(orderDetails) && !orderDetails.pickup_datetime) ||
        remainingCost <= 0 ||
        !hasOrderItems(orderDetails)
    );
};

export const shouldDisableOrderPreProcessActionButton = (orderDetails?: OrderDataLike): boolean => (
    isOrderRejected(orderDetails) ||
    !hasOrderItems(orderDetails)
);

export const canAddOrCreateCourierRequest = (orderDetails?: OrderDataLike): boolean => (
    !hasCourierRequestBeenSent(orderDetails) &&
    !isOrderPaid(orderDetails) &&
    !isOrderDelivered(orderDetails) &&
    !isOrderRejected(orderDetails)
);

export const shouldShowAcceptOnlyButtonInDeliveryModal = (orderDetails?: OrderDataLike): boolean => (
    needsOrderAcceptance(orderDetails) &&
    (
        isPickupOrder(orderDetails) ||
        !isWaiterCreatedOrder(orderDetails) ||
        hasCourierRequestBeenSent(orderDetails)
    )
);

export const shouldShowCompleteSetupButtonInDeliveryModal = (orderDetails?: OrderDataLike): boolean => (
    !isPickupOrder(orderDetails) &&
    canAddOrCreateCourierRequest(orderDetails) &&
    !shouldShowAcceptOnlyButtonInDeliveryModal(orderDetails)
);

export const shouldShowOrderActionDescriptionInDeliveryModal = (orderDetails?: OrderDataLike): boolean => (
    shouldShowAcceptOnlyButtonInDeliveryModal(orderDetails) ||
    shouldShowCompleteSetupButtonInDeliveryModal(orderDetails)
);

export const canEditPickupTimeInDeliveryModal = (orderDetails?: OrderDataLike): boolean => (
    // canAddOrCreateCourierRequest(orderDetails) && isOrderSuspended(orderDetails);
    isPickupOrder(orderDetails) || isOrderSuspended(orderDetails)
);

export const shouldDisableDeliveryAddressEditing = (orderDetails?: OrderDataLike): boolean => (
    !isWaiterCreatedOrder(orderDetails) || hasCourierRequestBeenSent(orderDetails)
);

export const canRejectOrder = (orderDetails: OrderDataLike | undefined, waiterPermissions?: WaiterPermissions | undefined): boolean => (
    !!orderDetails &&
    !!waiterPermissions?.cancel_order &&
    !isOrderRejected(orderDetails) && (
        !isDeliveryOrder(orderDetails) ||
        !isDetailedOrderData(orderDetails) ||
        !orderDetails.delivery ||
        DELIVERY_CANCELLABLE_STATUSES.includes(orderDetails.delivery.delivery_status)
    )
);

export const shouldShowRejectOrderButtonInDeliveryModal = (orderDetails: OrderDataLike | undefined, waiterPermissions: WaiterPermissions | undefined): boolean => (
    // !isPickupOrder &&
    // needsAcceptance &&
    // !isWaiterDraftOrder &&
    // canAddOrCreateCourierRequest &&

    !isWaiterCreatedOrder(orderDetails) &&
    canRejectOrder(orderDetails, waiterPermissions)
);

export const getOrderUiConditions = (orderDetails?: OrderDataLike, waiterPermissions?: WaiterPermissions) => ({
    isDeliveryOrder: isDeliveryOrder(orderDetails),
    isPickupOrder: isPickupOrder(orderDetails),
    isDeliveryOrPickupOrder: isDeliveryOrPickupOrder(orderDetails),
    isWaiterCreatedOrder: isWaiterCreatedOrder(orderDetails),
    isOrderSuspended: isOrderSuspended(orderDetails),
    isOrderDelivered: isOrderDelivered(orderDetails),
    isOrderRejected: isOrderRejected(orderDetails),
    isOrderPaid: isOrderPaid(orderDetails),
    needsOrderAcceptance: needsOrderAcceptance(orderDetails),
    canWaiterEditUnconfirmedOrder: canWaiterEditUnconfirmedOrder(orderDetails),
    getOrderEditLockReasonKey: getOrderEditLockReasonKey(orderDetails),
    hasCourierRequestBeenSent: hasCourierRequestBeenSent(orderDetails),
    hasPickupDateBeenSet: hasPickupDateBeenSet(orderDetails),
    hasOrderItems: hasOrderItems(orderDetails),
    canEditOrderOrAddItemsToIt: canEditOrderOrAddItemsToIt(orderDetails),
    shouldShowOrderPreProcessBox: shouldShowOrderPreProcessBox(orderDetails),
    shouldDisableOrderPreProcessActionButton: shouldDisableOrderPreProcessActionButton(orderDetails),
    shouldHidePaymentButton: shouldHidePaymentButton(orderDetails),
    shouldDisablePaymentButton: shouldDisablePaymentButton(orderDetails),
    canAddOrCreateCourierRequest: canAddOrCreateCourierRequest(orderDetails),
    shouldShowAcceptOnlyButtonInDeliveryModal: shouldShowAcceptOnlyButtonInDeliveryModal(orderDetails),
    shouldShowCompleteSetupButtonInDeliveryModal: shouldShowCompleteSetupButtonInDeliveryModal(orderDetails),
    shouldShowOrderActionDescriptionInDeliveryModal: shouldShowOrderActionDescriptionInDeliveryModal(orderDetails),
    canEditPickupTimeInDeliveryModal: canEditPickupTimeInDeliveryModal(orderDetails),
    shouldDisableDeliveryAddressEditing: shouldDisableDeliveryAddressEditing(orderDetails),
    canRejectOrder: canRejectOrder(orderDetails, waiterPermissions),
    shouldShowRejectOrderButtonInDeliveryModal: shouldShowRejectOrderButtonInDeliveryModal(orderDetails, waiterPermissions),
});
